import { useLayoutEffect, useState } from 'react';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Box, Typography } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
// project-imports
import NavGroup from './NavGroup';
import userType from 'menu-items';
import { generateDashboardMenu } from 'menu-items/dashboard';


import useConfig from 'hooks/useConfig';
import { HORIZONTAL_MAX_ITEM } from 'config';

// types
import { NavItemType } from 'types/menu';
import { LAYOUT_CONST } from 'types/config';
import useAuth from 'hooks/useAuth';
import getAdminUsersMenu from 'menu-items/users';
import { getAwardingBodyUsersMenu } from 'menu-items/users';
import Batches, {
  filterBatchesMenuByNcvetCompliance,
  filterNcvetQuestionBankMenuByOrgAccess
} from 'menu-items/batches';
import CertificateTemplate from 'menu-items/certificate';
import Tickets from 'menu-items/ticket';
import { canManageNcvetQuestionBankContent, isOrganizationNcvetEffective } from 'utils/ncvetQuestionBankAccess';

// ==============================|| DRAWER CONTENT - NAVIGATION ||============================== //

const Navigation = () => {
  const theme = useTheme();

  const downLG = useMediaQuery(theme.breakpoints.down('lg'));

  const { menuOrientation } = useConfig();


  const [selectedItems, setSelectedItems] = useState<string | undefined>('');
  const [selectedLevel, setSelectedLevel] = useState<number>(0);
  const [menuItems, setMenuItems] = useState<{ items: NavItemType[] }>({ items: [] });
  const { user, organization }: any = useAuth();
  const itemPermission = user?.userTypeId?.permission;
  const getDash = generateDashboardMenu(user);
  const canNcvetQuestionBank = canManageNcvetQuestionBankContent(organization, user);
  const ncvetEffective = isOrganizationNcvetEffective(organization);
  const batchesForNcvet = filterNcvetQuestionBankMenuByOrgAccess(
    filterBatchesMenuByNcvetCompliance(Batches, ncvetEffective),
    canNcvetQuestionBank
  );
  let itemData: NavItemType[] = [];

  if (user?.type === 'admin') {
    itemData = [
      getAdminUsersMenu(ncvetEffective, !organization?.parentOrganizationId),
      ...(userType.admin || []).map((navItem) => (navItem.id === 'NCVETComplaince' ? batchesForNcvet : navItem))
    ];
  } else if (user?.type === 'awardingBody' || user?.type === 'awarding-body') {
    itemData = [
      getDash,
      getAwardingBodyUsersMenu(),
      batchesForNcvet,
      CertificateTemplate,
      Tickets
    ];
  } else if (user?.type === 'assessorAdmin' || user?.type === 'assessor') {
    itemData = [
      getDash,
      getAwardingBodyUsersMenu(),
      batchesForNcvet,
      CertificateTemplate,
      Tickets
    ];
  } else if (user?.type === 'faculty') {
    itemData = userType.faculty || [];
  } else if (user?.type === 'superadmin') {
    itemData = userType.superadmin || [];
  }
  const menuItem: any = {
    items: itemData
  };
  function childPermission(item: any) {
    const isRootOrganization = !organization?.parentOrganizationId;
    if (item?.name === 'NCVETComplaince') {
      const allChildren = item?.children?.[0]?.children || [];

      const ncvetChildIdsForSubOrg = ['Batch', 'ncvetQuestions', 'ncvetRubrics'];

      let newChildItems = isRootOrganization
        ? allChildren
        : allChildren.filter((childItem: any) => ncvetChildIdsForSubOrg.includes(childItem.id));

      if (!ncvetEffective) {
        newChildItems = newChildItems.filter((childItem: any) => !childItem.requiresNcvetCompliance);
      }

      item.children[0].children = newChildItems;
      return item;
    }

    const newChildItems = item?.children?.[0]?.children?.filter((childItem: any) => {
      if (childItem.name === 'CourseReassign') {
        return organization?.trainingMode ? organization?.trainingMode : false;
      }
      return itemPermission?.[item?.name]?.[childItem?.id] === true;
    });
    item.children[0].children = newChildItems;
    return item;
  }
  function CoursePermission(item: any) {
    const newChildItems = item?.children?.[0]?.children?.filter((childItem: any) => {
      if (childItem.name === 'CourseReassign') {
        return organization?.trainingMode && itemPermission?.[childItem?.name]?.View;
      } else if (childItem.name === 'OMRConfig') {
        return itemPermission?.[childItem?.name]?.Update;
      }
      return itemPermission?.[childItem?.name]?.View === true;
    });
    item.children[0].children = newChildItems;
    return item;
  }
  function getPermissionItems(menuItem: any) {
    if (user?.type !== 'superadmin') {
      // Awarding Body: skip permission-filtering; show only its limited module
      if (user?.type === 'awardingBody' || user?.type === 'awarding-body') {
        // Awarding Body can manage assessors; show Dashboard + Users + NCVET + Certificates + Tickets
        setMenuItems({
          items: [getDash, getAwardingBodyUsersMenu(), batchesForNcvet, CertificateTemplate, Tickets]
        });
        return;
      }
      const keys = Object.keys(itemPermission || {});
      const newMenuItem = menuItem?.items?.filter((item: any) => {
        if (keys.includes(item?.name)) {
          if (item?.name === 'Courses' || item?.name === 'Exam') {
            return CoursePermission(item);
          }

          if (item?.name === 'NCVETComplaince') {
            return childPermission(item);
          }
          if (
            itemPermission?.[item?.name]?.Users === true ||
            itemPermission?.[item?.name]?.Courses === true ||
            itemPermission?.[item?.name]?.Assessments === true
          ) {
            return childPermission(item);
          }
          if (item?.name === 'Meeting' && organization?.ncvetCompliance) {
            return false;
          }
          if (itemPermission?.[item?.name]?.View === true) {
            return true;
          }
        }
        return false;
      });
      if (itemPermission?.Dashboard?.View) {
        newMenuItem?.splice(0, 0, getDash);
      }
      setMenuItems({ items: newMenuItem });
    } else {
      if (menuItem?.items?.[0]?.name !== 'Dashboard') menuItem?.items?.splice(0, 0, getDash);
      setMenuItems(menuItem);
    }
  }
  useLayoutEffect(() => {
    if (user && menuItem?.items?.length > 0) {
      getPermissionItems(menuItem);
    }
    // eslint-disable-next-line
  }, [user, organization]);

  const isHorizontal = menuOrientation === LAYOUT_CONST.HORIZONTAL_LAYOUT && !downLG;

  const lastItem = isHorizontal ? HORIZONTAL_MAX_ITEM : null;
  let lastItemIndex = menuItems?.items?.length - 1;
  let remItems: NavItemType[] = [];
  let lastItemId: string;

  if (lastItem && lastItem < menuItems?.items?.length) {
    lastItemId = menuItems?.items[lastItem - 1].id!;
    lastItemIndex = lastItem - 1;
    remItems = menuItems?.items?.slice(lastItem - 1, menuItems?.items?.length).map((item) => ({
      title: item.title,
      elements: item.children,
      icon: item.icon
    }));
  }

  const navGroups = menuItems?.items
    ?.slice(0, lastItemIndex + 1)
    .map((item) => {
      if (item.type === 'group') {
        return (
          <NavGroup
            key={item.id}
            setSelectedItems={setSelectedItems}
            setSelectedLevel={setSelectedLevel}
            selectedLevel={selectedLevel}
            selectedItems={selectedItems}
            lastItem={lastItem!}
            remItems={remItems}
            lastItemId={lastItemId}
            item={item}
          />
        );
      }

      return (
        <Typography key={item.id} variant="h6" color="error" align="center">
          Fix - Navigation Group
        </Typography>
      );
    });

  return (
    <Box
      sx={{

        '& > ul:first-of-type': { mt: 1 },
        display: isHorizontal ? { xs: 'block', lg: 'flex' } : 'block'

      }}
    >
      {navGroups}
    </Box>
  );
};

export default Navigation;
